import React, { useState, useEffect } from 'react';
import { DataGrid, GridToolbar, esES } from '@mui/x-data-grid';
import { Tooltip, Button } from '@mui/material';
import { HeaderTracking } from '../componentsLanding/HeaderTracking';
import { getOrders } from '../../../actions/orders';
import { LinearWithValueLabel } from '../../orders/ProgressBar';
import { formatDateMX } from '../../../helpers/hours';
import { HistoryTimelineModal } from '../../tracking/HistoryTimeLine';
import {
	blue_pilu,
	formatterCurrency,
	handleError,
	white_pilu,
} from '../../../helpers/utils';
import { CLIENT_TYPES } from '../../utils/lists';
import { getBiases, getSubBiases } from '../../../actions/biases';
import { useSelector } from 'react-redux';
import { ClientHeader } from '../componentsLanding/ClientHeader';

{
	/** Helper functions */
}
const fetchOrders = async (user, setOrders) => {
	try {
		const endpoint = `api/order/?ordering=-created_at&client_id__id=${user?.id}`;
		const response = await getOrders(endpoint);
		const ordersData = response.data.results.map((order) => ({
			id: order.id,
			number_order: order.number_order,
			biases_count: order.biases_count,
			company_name: order.company_name,
			client_type:
				CLIENT_TYPES.find((type) => type.name === order.type_of_client)
					?.label || order.type_of_client,
			zone: `${order.client_city}, ${order.client_state}`,
			division: order.division,
			canceled: order.canceled,
			amount: order.amount,
			progress: order.progress,
			created_at: formatDateMX(order.created_at),
			validation_date: order.validation_date
				? formatDateMX(order.validation_date)
				: 'No asignado',
			authorization_date: order.authorization_date
				? formatDateMX(order.authorization_date)
				: 'No asignado',
			delivery_date: formatDateMX(order.delivery_date),
			delivery_forecast: formatDateMX(order.delivery_forecast),
			status: order.status,
		}));
		setOrders(ordersData);
	} catch (error) {
		handleError(error, 'Ocurrió un error al intentar obtener los pedidos');
	}
};

const fetchBiases = async (orderId, setCurrentBiases) => {
	try {
		const { data } = await getBiases(orderId);
		setCurrentBiases(data.results);
	} catch (error) {
		handleError(error, 'Ocurrió un error al intentar obtener las partidas');
	}
};

const fetchSubBiases = async (biasId, setCurrentSubBiases, setSubBiasInfo) => {
	try {
		const { data } = await getSubBiases(biasId);
		setCurrentSubBiases(data.results);
		setSubBiasInfo(data.results);
	} catch (error) {
		handleError(
			error,
			'Ocurrió un error al intentar obtener las subpartidas'
		);
	}
};

{
	/** DataGrid Columns Configuration */
}
const columnStyleCell = {
	headerClassName: 'column-header-pilu',
	headerAlign: 'center',
};

const columns = (handleOpenModal) => [
	{
		field: 'actions',
		...columnStyleCell,
		headerName: 'Acciones',
		width: 180,
		renderCell: ({ row }) => (
			<Button
				style={{ color: white_pilu, backgroundColor: blue_pilu }}
				variant='contained'
				onClick={() => handleOpenModal(row.id)}
			>
				Ver progreso
			</Button>
		),
	},
	{
		field: 'number_order',
		...columnStyleCell,
		headerName: 'Número de pedido',
		width: 180,
		renderCell: ({ row }) => (
			<Tooltip title={row.number_order}>
				<span className='table-cell-trucate'>{row.number_order}</span>
			</Tooltip>
		),
	},
	{
		field: 'biases_count',
		...columnStyleCell,
		headerName: 'Número de partidas',
		width: 180,
		renderCell: ({ row }) => (
			<Tooltip title={row.biases_count}>
				<span className='table-cell-trucate'>{row.biases_count}</span>
			</Tooltip>
		),
	},
	{
		field: 'amount',
		...columnStyleCell,
		headerName: 'Importe total',
		width: 170,
		renderCell: ({ row }) => (
			<Tooltip title={formatterCurrency(row.amount)}>
				<span className='table-cell-trucate'>
					{formatterCurrency(row.amount)}
				</span>
			</Tooltip>
		),
	},
	{
		field: 'progress',
		...columnStyleCell,
		headerName: 'Progreso',
		width: 170,
		renderCell: ({ row }) => (
			<LinearWithValueLabel progress={row.progress} />
		),
		valueGetter: ({ row }) => row.progress,
	},
	{
		field: 'created_at',
		...columnStyleCell,
		headerName: 'Fecha de creación',
		width: 160,
		renderCell: ({ row }) => (
			<Tooltip title={row.created_at}>
				<span className='table-cell-trucate'>{row.created_at}</span>
			</Tooltip>
		),
	},
	{
		field: 'validation_date',
		...columnStyleCell,
		headerName: 'Fecha de validación',
		width: 160,
		renderCell: ({ row }) => (
			<Tooltip title={row.validation_date}>
				<span className='table-cell-trucate'>
					{row.validation_date}
				</span>
			</Tooltip>
		),
	},
	{
		field: 'authorization_date',
		...columnStyleCell,
		headerName: 'Fecha de autorización',
		width: 180,
		renderCell: ({ row }) => (
			<Tooltip title={row.authorization_date}>
				<span className='table-cell-trucate'>
					{row.authorization_date}
				</span>
			</Tooltip>
		),
	},
	{
		field: 'delivery_date',
		...columnStyleCell,
		headerName: 'Fecha de entrega',
		width: 160,
		renderCell: ({ row }) => (
			<Tooltip title={row.delivery_date}>
				<span className='table-cell-trucate'>{row.delivery_date}</span>
			</Tooltip>
		),
	},
	{
		field: 'delivery_forecast',
		...columnStyleCell,
		headerName: 'Fecha estimada',
		width: 160,
		renderCell: ({ row }) => (
			<Tooltip title={row.delivery_forecast}>
				<span className='table-cell-trucate'>
					{row.delivery_forecast}
				</span>
			</Tooltip>
		),
	},
];

{
	/** OrdersTable Component */
}
const OrdersTable = ({ orders, handleOpenModal, client }) => (
	<div
		style={{
			height: 400,
			width: '97%',
			margin: '20px',
			marginTop: '120px',
		}}
	>
		<h2 style={{ marginBottom: '20px' }}>
			Mis pedidos - {client.company_name}
		</h2>
		<DataGrid
			rows={orders}
			columns={columns(handleOpenModal)}
			components={{ Toolbar: GridToolbar }}
			localeText={esES.components.MuiDataGrid.defaultProps.localeText}
		/>
	</div>
);

{
	/** MyOrders Component */
}
export const MyOrders = ({ showHeader = true }) => {
	const [orders, setOrders] = useState([]);
	const [modalOpen, setModalOpen] = useState(false);
	const [currentOrderId, setCurrentOrderId] = useState(null);
	const [currentBiases, setCurrentBiases] = useState([]);
	const [currentSubBiases, setCurrentSubBiases] = useState([]);
	const [orderInfo, setOrderInfo] = useState(null);
	const [subBiasInfo, setSubBiasInfo] = useState([]);
	const { user } = useSelector((state) => state);

	useEffect(() => {
		// console.log(user?.company);
		fetchOrders(user?.company, setOrders);
		localStorage.setItem('lastPathPilu', '/tracking/pedidos-tabla');
	}, []);

	const handleOpenModal = (orderId) => {
		const selectedOrder = orders.find((order) => order.id === orderId);
		setOrderInfo(selectedOrder);
		setCurrentOrderId(orderId);
		setModalOpen(true);
		fetchBiases(orderId, setCurrentBiases);
	};

	const handleCloseModal = () => {
		setModalOpen(false);
		setCurrentOrderId(null);
		setCurrentBiases([]);
		setCurrentSubBiases([]);
		setOrderInfo(null);
		setSubBiasInfo([]);
	};

	return (
		<>
			<HeaderTracking />

			<HistoryTimelineModal
				open={modalOpen}
				onClose={handleCloseModal}
				orderData={orderInfo}
				biases={currentBiases}
				subBiases={currentSubBiases}
				subBiasInfo={subBiasInfo}
				handleGetSubBiases={(biasId) =>
					fetchSubBiases(biasId, setCurrentSubBiases, setSubBiasInfo)
				}
			/>
			<OrdersTable
				orders={orders}
				client={user.company}
				handleOpenModal={handleOpenModal}
			/>
		</>
	);
};
